import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { contractApi } from "@frontend/features/contracts";
import type { ContractRow } from "@frontend/features/contracts";
import { jobApi } from "@frontend/features/jobs";
import { ApiError } from "@frontend/lib/http";

import { projectApi } from "../services";
import type { InterventionRow, JobRow } from "../types";

type ListKey = "jobs" | "interventions" | "contracts";

export type ProjectListsState = {
  projectId: number | null;
  jobs: JobRow[];
  interventions: InterventionRow[];
  contracts: ContractRow[];
  loaded: Record<ListKey, boolean>;
  jobsLoading: boolean;
  interventionsLoading: boolean;
  contractsLoading: boolean;
  error: string | null;
};

export const initialProjectListsState: ProjectListsState = {
  projectId: null,
  jobs: [],
  interventions: [],
  contracts: [],
  loaded: { jobs: false, interventions: false, contracts: false },
  jobsLoading: false,
  interventionsLoading: false,
  contractsLoading: false,
  error: null,
};

export const projectListsAtom = atomWithImmer<ProjectListsState>(
  initialProjectListsState,
);

function listLoader<K extends ListKey>(
  key: K,
  fetch: (projectId: number) => Promise<ProjectListsState[K]>,
) {
  const loading = `${key}Loading` as const;

  return atom(null, async (get, set, projectId: number): Promise<void> => {
    const state = get(projectListsAtom);

    if (state.projectId === projectId && state.loaded[key]) {
      return;
    }

    set(projectListsAtom, (draft) => {
      if (draft.projectId !== projectId) {
        Object.assign(draft, initialProjectListsState, { projectId });
      }

      draft[loading] = true;
      draft.error = null;
    });

    try {
      const rows = await fetch(projectId);

      set(projectListsAtom, (draft) => {
        if (draft.projectId !== projectId) {
          return;
        }

        draft[key] = rows;
        draft.loaded[key] = true;
        draft[loading] = false;
      });
    } catch (error: unknown) {
      set(projectListsAtom, (draft) => {
        if (draft.projectId !== projectId) {
          return;
        }

        draft[loading] = false;
        draft.error = message(error);
      });
    }
  });
}

export const loadJobsAtom = listLoader("jobs", (projectId) =>
  projectApi.fetchJobs(projectId),
);

export const loadInterventionsAtom = listLoader("interventions", (projectId) =>
  projectApi.fetchInterventions(projectId),
);

export const loadContractsAtom = listLoader("contracts", (projectId) =>
  contractApi.fetchContracts(projectId),
);

export const refreshJobsAtom = atom(null, async (get, set): Promise<void> => {
  const { projectId } = get(projectListsAtom);

  if (null === projectId) {
    return;
  }

  set(projectListsAtom, (draft) => {
    draft.jobs = [];
    draft.loaded.jobs = false;
  });

  await set(loadJobsAtom, projectId);
});

export const deleteJobAtom = atom(
  null,
  async (get, set, jobId: number): Promise<boolean> => {
    const { projectId } = get(projectListsAtom);

    if (null === projectId) {
      return false;
    }

    set(projectListsAtom, (draft) => {
      draft.error = null;
    });

    try {
      await jobApi.deleteJob(projectId, jobId);
    } catch (error: unknown) {
      set(projectListsAtom, (draft) => {
        draft.error = refusal(error);
      });

      return false;
    }

    await set(refreshJobsAtom);

    return true;
  },
);

export const refreshContractsAtom = atom(
  null,
  async (get, set): Promise<void> => {
    const { projectId } = get(projectListsAtom);

    if (null === projectId) {
      return;
    }

    set(projectListsAtom, (draft) => {
      draft.contracts = [];
      draft.loaded.contracts = false;
    });

    await set(loadContractsAtom, projectId);
  },
);

export const deleteContractAtom = atom(
  null,
  async (get, set, contractId: number): Promise<boolean> => {
    const { projectId } = get(projectListsAtom);

    if (null === projectId) {
      return false;
    }

    set(projectListsAtom, (draft) => {
      draft.error = null;
    });

    try {
      await contractApi.deleteContract(projectId, contractId);
    } catch (error: unknown) {
      set(projectListsAtom, (draft) => {
        draft.error = refusal(error);
      });

      return false;
    }

    await set(refreshContractsAtom);

    return true;
  },
);

export const refreshInterventionsAtom = atom(
  null,
  async (get, set): Promise<void> => {
    const { projectId } = get(projectListsAtom);

    if (null === projectId) {
      return;
    }

    set(projectListsAtom, (draft) => {
      draft.interventions = [];
      draft.loaded.interventions = false;
    });

    await set(loadInterventionsAtom, projectId);
  },
);

export const deleteInterventionAtom = atom(
  null,
  async (_get, set, interventionId: number): Promise<boolean> => {
    set(projectListsAtom, (draft) => {
      draft.error = null;
    });

    try {
      await projectApi.deleteProject(interventionId);
    } catch (error: unknown) {
      set(projectListsAtom, (draft) => {
        draft.error = refusal(error);
      });

      return false;
    }

    await set(refreshInterventionsAtom);

    return true;
  },
);

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function refusal(error: unknown): string {
  if (error instanceof ApiError && 0 < error.violations.length) {
    return error.violations.map((violation) => violation.message).join(" ");
  }

  return message(error);
}
